import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinSearchAccordionComponent } from '../../shared/design-system/search-accordion.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { ComplianceQueueItemRecord, FinanceApiService } from '../finance/finance-api.service';

@Component({
  standalone: true,
  selector: 'ain-platform-compliance-queue',
  imports: [AinPageComponent, AinSearchAccordionComponent, AinStateComponent, InfoHintComponent, FormsModule, TableModule, ButtonModule],
  template: `
    <ain-page
      [eyebrow]="t('platform.eyebrow')"
      [title]="t('platform.complianceQueue.pageTitle')"
      [description]="t('platform.complianceQueue.pageDescription')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'partyHub.help.complianceQueue.title'"
        [descriptionKey]="'partyHub.help.complianceQueue.description'"
        [bulletKeys]="[
          'partyHub.help.complianceQueue.bullets.reviewParty',
          'partyHub.help.complianceQueue.bullets.sharedCompliance',
          'partyHub.help.complianceQueue.bullets.customerLink'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <ain-search-accordion [title]="t('platform.search.title')">
          <section class="search-grid">
            <input [(ngModel)]="filters.query" [placeholder]="t('platform.complianceQueue.searchPlaceholder')" />
            <button pButton type="button" [label]="t('platform.search.apply')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('platform.search.clear')" (click)="clearFilters()"></button>
          </section>
        </ain-search-accordion>

        @if (!rows().length) {
          <ain-state type="empty" [title]="t('platform.complianceQueue.empty')" />
        } @else {
          <p-table [value]="rows()">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('platform.complianceQueue.fields.party') }}</th>
                <th>{{ t('platform.complianceQueue.fields.customer') }}</th>
                <th>{{ t('platform.parties.fields.kind') }}</th>
                <th>{{ t('finance.customers.compliance.kycStatus') }}</th>
                <th>{{ t('finance.customers.compliance.kybStatus') }}</th>
                <th>{{ t('platform.complianceQueue.fields.linkedCustomers') }}</th>
                <th>{{ t('platform.complianceQueue.fields.actions') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.partyDisplayName }}</td>
                <td>{{ customerLabel(row) }}</td>
                <td>{{ kindLabel(row.partyKind) }}</td>
                <td>{{ complianceStatusLabel(row.kycStatus) }}</td>
                <td>{{ complianceStatusLabel(row.kybStatus) }}</td>
                <td>{{ row.linkedCustomerCount }}</td>
                <td class="actions">
                  <button
                    pButton
                    type="button"
                    size="small"
                    [label]="t('platform.complianceQueue.actions.reviewParty')"
                    text="true"
                    (click)="openParty(row.partyId)">
                  </button>
                  @if (row.primaryCustomerId) {
                    <button
                      pButton
                      type="button"
                      size="small"
                      [label]="t('platform.complianceQueue.actions.openCustomer')"
                      text="true"
                      (click)="openCustomer(row.primaryCustomerId)">
                    </button>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
      }

      @if (errorMessage()) {
        <p class="message error">{{ errorMessage() }}</p>
      }
    </ain-page>
  `,
  styles: [`
    .search-grid { display: grid; grid-template-columns: 2fr auto auto; gap: 8px; align-items: center; }
    .search-grid input { height: 38px; border: 1px solid #cbd5e1; border-radius: 8px; padding: 0 10px; width: 100%; }
    .actions { display: flex; flex-wrap: wrap; gap: 4px; }
    .message.error { margin-top: 10px; color: #b91c1c; }
    @media (max-width: 1200px) { .search-grid { grid-template-columns: 1fr; } }
  `]
})
export class PlatformComplianceQueueComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);

  readonly loading = signal(false);
  readonly rows = signal<ComplianceQueueItemRecord[]>([]);
  readonly errorMessage = signal('');
  readonly canLoad = computed(() => !this.loading());

  filters = {
    query: ''
  };

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    if (!this.canLoad()) {
      return;
    }

    this.loading.set(true);
    this.errorMessage.set('');
    this.api.listPendingKycKybComplianceQueue({
      query: this.filters.query,
      skip: 0,
      take: 200
    }).subscribe({
      next: response => {
        this.rows.set(response.items);
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.errorMessage.set(this.t('common.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  clearFilters(): void {
    this.filters.query = '';
    this.load();
  }

  openParty(partyId: string): void {
    void this.router.navigate(['/platform/parties', partyId], {
      queryParams: { tab: 'compliance' }
    });
  }

  openCustomer(customerId: string): void {
    void this.router.navigate(['/financial/payments/customers', customerId]);
  }

  customerLabel(row: ComplianceQueueItemRecord): string {
    if (row.primaryCustomerCode && row.primaryCustomerDisplayName) {
      return `${row.primaryCustomerCode} — ${row.primaryCustomerDisplayName}`;
    }

    if (row.primaryCustomerDisplayName) {
      return row.primaryCustomerDisplayName;
    }

    return '-';
  }

  kindLabel(value: number | string): string {
    const numeric = typeof value === 'string' ? Number(value) : value;
    const map: Record<number, string> = {
      0: this.t('platform.parties.kind.individual'),
      1: this.t('platform.parties.kind.organization'),
      2: this.t('platform.parties.kind.device'),
      3: this.t('platform.parties.kind.aiAgent'),
      4: this.t('platform.parties.kind.service')
    };
    return map[numeric] ?? `${value}`;
  }

  complianceStatusLabel(value: number | string): string {
    const numeric = typeof value === 'string' ? Number(value) : value;
    const map: Record<number, string> = {
      0: this.t('finance.customers.compliance.status.notStarted'),
      1: this.t('finance.customers.compliance.status.pending'),
      2: this.t('finance.customers.compliance.status.passed'),
      3: this.t('finance.customers.compliance.status.failed'),
      4: this.t('finance.customers.compliance.status.waived')
    };
    return map[numeric] ?? `${value}`;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
